import React, { FC, memo, useState, useEffect, useCallback } from 'react'

import { Swiper as SwiperType } from 'swiper'

import { PREVIEW_URL } from '@sportworld/js-sdk/src/envs/envs'

import { Header } from '@/components/Header'
import { LineElem } from '@/components/Lines/styles'
import { Slider } from '@/components/Slider/Slider'
import { sliderMovementType } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { smartPreviewSlidersCountInPortView } from '@/constants/tiles'
import { SettingsContainer } from '@/containers/styles'
import { useOpenAppController } from '@/hooks/useOpenAppController'
import { OpenTileProps } from '@/hooks/useOpenTile'
import { SlideDataType } from '@/types/sliderTypes'
import { showError } from '@/utils/utils'

const nodeId = `${focusIds.SETTINGS_SMART_PREVIEW}${focusIds.CONTAINER_MOD}`

const SmartPreviewRaw: FC = () => {
  const [data, setData] = useState<any>([])
  const [swiper, setSwiper] = useState<Nullable<SwiperType>>(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)

  const deepLinkSwitcher = useOpenAppController()

  useEffect(() => {
    fetch(PREVIEW_URL)
      .then((res) => res.json())
      .then((data) => {
        const tiles = data?.sections[0]?.tiles?.map((t: any, i: number) => ({
          ...t,
          id: i,
          image: {
            id: null,
            sources: [
              {
                darkTheme: false,
                format: 'Jpeg',
                height: 1080,
                url: t.image_url,
                width: 1920,
              },
            ],
          },
          type: 'Event',
        }))
        setData(tiles)
      })
  }, [])

  // TODO: not working in latest releases. Also UI looks wrong. Need to fix.
  const formatDataForDeepLinkSwitcher = (obj: SlideDataType) => {
    let data
    const jsonActionData =
      obj?.action_data
        ?.replace(/\\(u0022)/g, '\\\\u0022')
        .replace(/\\(n)/g, ' ') || ''
    let parsedActionData
    try {
      parsedActionData = JSON.parse(jsonActionData)
    } catch (err) {
      showError('Json Data is not valid')
      return
    }

    try {
      if (parsedActionData.id) {
        data = { dataFromPreviewTiles: parsedActionData }
      }
      if (parsedActionData.assetId) {
        data = { dataFromUniversalGuide: parsedActionData.assetId }
      }
      if (parsedActionData.SCOUPONCODE) {
        data = { dataFromPromotion: parsedActionData }
      }
    } catch (err) {
      showError('Json Data is not valid')
      return
    }
    data && deepLinkSwitcher(data)
  }

  const onSwiper = (swiper: SwiperType) =>
    setTimeout(() => {
      setSwiper(swiper)
      swiper.slideTo(activeSlideIndex, 0)
    }, 0)

  const slideNext = (e?: LRUDEvent) => {
    e?.preventDefault()
    swiper && setActiveSlideIndex(swiper?.activeIndex)
  }

  const slidePrev = (e?: LRUDEvent) => {
    if (swiper && swiper.activeIndex >= 0 && activeSlideIndex) {
      e?.preventDefault()
      swiper && setActiveSlideIndex(swiper.activeIndex)
    }
  }

  const onHandleToggle = useCallback(({ tileData }: OpenTileProps) => {
    formatDataForDeepLinkSwitcher(tileData)
  }, [])

  const onSelected = () => {
    const tileData = data[activeSlideIndex]
    const focusId = `${nodeId}_${tileData.id}_${activeSlideIndex}`
    onHandleToggle({ tileData, focusId })
  }

  return (
    <SettingsContainer>
      <Header />
      <LineElem
        elementType="section"
        focusId={nodeId}
        onRight={slideNext}
        onLeft={slidePrev}
        className="line"
        $isVisible={true}
        onSelected={onSelected}
      >
        <Slider
          swiper={swiper}
          slidesPerView={smartPreviewSlidersCountInPortView}
          sliderId={nodeId}
          sliderData={data}
          onSwiper={onSwiper}
          setVirtual={true}
          initialSlide={activeSlideIndex}
          setActiveSlideIndex={setActiveSlideIndex}
          movementType={sliderMovementType.DEFAULT}
          broadcastId="broadcastId"
        />
      </LineElem>
    </SettingsContainer>
  )
}

export const SmartPreview = memo(SmartPreviewRaw)
